import { makeAssociationValuesCache } from './instances/associationValuesCache';
import { makeCallsSettingsCache } from './instances/callsSettings';
import { makeConditionalPropertyOptionsRulesCache } from './instances/conditionalPropertyOptionsRulesCache';
import { makeHubsSettingsCache } from './instances/hubsSettingsCache';
import { makeLeadFollowUpSettingsCache } from './instances/leadFollowUpSettingsCache';
import { makeLeadPipelineSettingsCache } from './instances/leadPipelineSettingsCache';
import { makeLeadSettingsCache } from './instances/leadSettingsCache';
import { makeMultiCurrencyCache } from './instances/multiCurrency';
import { makePipelineStageValidationCache } from './instances/pipelineStageValidationCache';
import { makePipelinesCache } from './instances/pipelinesCache';
import { makeProductAssociatedBundlesCache } from './instances/productAssociatedBundlesCache';
import { makePropertyValuesCache } from './instances/propertyValuesCache';
import { makeTaxCategoriesCache } from './instances/taxCategoriesCache';
import { makeUrlValidationCache } from './instances/validateUrlCache';
import { makePropertyValidationRulesCache } from './instances/validationRulesCache';
import { makeMarketplaceAppStatusCache } from './instances/marketplaceAppStatusCache';
import { makeTaskSettingsCache } from './instances/taskSettingsCache';

/*
 * A note on our caching strategy:
 *
 * customer-data-properties mounts a ton of inputs, formatters, and other components that
 * need the same data (like property values or validation rules). We don't want each one making its own
 * network request or becoming out of sync. Instead, they all share these singleton cache instances.
 *
 * For example:
 * const PropertyInputFoo = () => {
 *   const value = propertyValuesCache.get('contact', '123', 'firstname');
 *   return <input value={value} />;
 * };
 *
 * const PropertyInputBar = () => {
 *   const value = propertyValuesCache.get('contact', '123', 'firstname');
 *   return <span>{value}</span>;
 * };
 *
 * Both components share the same cached data; no duplicate network requests will be made.
 *
 * Generally, you should not need to create your own cache instances. These singletons exist so everyone
 * shares the same data. Making a new cache with `makePropertyValuesCache()` will break this strategy and
 * should only be done if you have some edge case that isn't solved by the existing caches.
 *
 * It is not recommended to use cache.clear() at any point, but it is exposed for testing purposes.
 */

export const callsSettingsCache = makeCallsSettingsCache();
export const conditionalPropertyOptionsRulesCache = makeConditionalPropertyOptionsRulesCache();
export const hubsSettingsCache = makeHubsSettingsCache();
export const leadFollowUpSettingsCache = makeLeadFollowUpSettingsCache();
export const leadPipelineSettingsCache = makeLeadPipelineSettingsCache();
export const multiCurrencyCache = makeMultiCurrencyCache();
export const leadSettingsCache = makeLeadSettingsCache();
export const pipelinesCache = makePipelinesCache();
export const pipelineStageValidationCache = makePipelineStageValidationCache();
export const propertyValidationRulesCache = makePropertyValidationRulesCache();
export const propertyValuesCache = makePropertyValuesCache();
export const associationValuesCache = makeAssociationValuesCache();
export const taxCategoriesCache = makeTaxCategoriesCache();
export const urlValidationCache = makeUrlValidationCache();
export const productAssociatedBundlesCache = makeProductAssociatedBundlesCache();
export const marketplaceAppStatusCache = makeMarketplaceAppStatusCache();
export const taskSettingsCache = makeTaskSettingsCache();

/**
 * Exported for testing purposes, generally we should not be clearing our caches at runtime. This
 * is intended to be used in tests to reset the cache between tests.
 */
export const clearAllCaches = () => {
  callsSettingsCache.clear();
  conditionalPropertyOptionsRulesCache.clear();
  hubsSettingsCache.clear();
  leadFollowUpSettingsCache.clear();
  leadPipelineSettingsCache.clear();
  leadSettingsCache.clear();
  multiCurrencyCache.clear();
  pipelinesCache.clear();
  pipelineStageValidationCache.clear();
  propertyValidationRulesCache.clear();
  propertyValuesCache.clear();
  associationValuesCache.clear();
  taxCategoriesCache.clear();
  urlValidationCache.clear();
  productAssociatedBundlesCache.clear();
  marketplaceAppStatusCache.clear();
  taskSettingsCache.clear();
};